/**
 * Judge.me トップページレビューフィルタリング・カスタマイズ
 * 星評価の低いレビューを非表示にし、表示件数を制限
 * 商品画像表示、レイアウト調整、カスタムフィールドの並べ替え
 */

(function() {
  'use strict';

  // 設定
  const WIDGET_SELECTOR = '#judgeme-homepage-reviews-widget';
  const REVIEW_SELECTOR = '.jdgm-rev';
  const RATING_SELECTOR = '.jdgm-rev__rating';
  const CHECK_INTERVAL = 200; // チェック間隔（ミリ秒）
  const MAX_ATTEMPTS = 50; // 最大試行回数（10秒）

  let attempts = 0;
  let isProcessed = false;

  // 商品画像のキャッシュ
  const productImageCache = new Map();

  // Swiperインスタンスをグローバルスコープに保存
  let swiperInstance = null;

  /**
   * レビューのフィルタリングと制限を実行
   */
  function filterAndLimitReviews() {
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) return false;

    const minRating = parseInt(widget.dataset.minRating) || 4;
    const maxReviews = parseInt(widget.dataset.maxReviews) || 10;

    const reviews = widget.querySelectorAll(REVIEW_SELECTOR);
    if (reviews.length === 0) return false;

    let visibleCount = 0;

    reviews.forEach((review) => {
      // 既に処理済みの場合はスキップ
      if (review.dataset.filtered) return;

      const ratingElement = review.querySelector(RATING_SELECTOR);
      if (!ratingElement) return;

      const rating = parseFloat(ratingElement.dataset.score) || 0;

      // 星評価でフィルタリング
      if (rating < minRating) {
        review.style.display = 'none';
        review.dataset.filtered = 'hidden';
      } else {
        visibleCount++;
        // 最大表示件数を超えたら非表示
        if (visibleCount > maxReviews) {
          review.style.display = 'none';
          review.dataset.filtered = 'hidden-by-limit';
        } else {
          review.dataset.filtered = 'visible';

          // カスタマイズ処理を実行
          cleanProductLink(review);
          reorderCustomFields(review);
          customizeReviewLayout(review);
          addProductImage(review); // 非同期、レイアウト後に画像追加
        }
      }
    });

    // ページネーションを非表示（全件表示ではないため）
    const pagination = widget.querySelector('.jdgm-paginate');
    if (pagination) {
      pagination.style.display = 'none';
    }

    // レビュー総数表示を更新
    updateReviewCount(widget, visibleCount);

    return true;
  }

  /**
   * レビュー総数表示を更新
   */
  function updateReviewCount(widget, visibleCount) {
    const countElement = widget.querySelector('.jdgm-rev-widg__summary-text');
    if (countElement && visibleCount > 0) {
      countElement.textContent = `${visibleCount}件のレビュー`;
    }
  }

  /**
   * 商品リンクからハッシュを削除
   */
  function cleanProductLink(review) {
    const productLink = review.querySelector('.jdgm-rev__prod-link');
    if (productLink && productLink.href) {
      productLink.href = productLink.href.split('#')[0];
    }
  }

  /**
   * 商品画像を取得して表示
   */
  async function addProductImage(review) {
    // 既に画像が追加されている場合はスキップ
    if (review.querySelector('.jdgm-rev__product-image')) {
      return;
    }

    // data属性から商品ハンドルを取得（customizeReviewLayoutで保存済み）
    const productHandle = review.dataset.productHandle;
    if (!productHandle) {
      return;
    }

    try {
      let imageUrl;
      let productTitle;

      // キャッシュから取得
      if (productImageCache.has(productHandle)) {
        const cached = productImageCache.get(productHandle);
        imageUrl = cached.imageUrl;
        productTitle = cached.title;
      } else {
        // APIから商品データを取得
        const response = await fetch(`/products/${productHandle}.json`);
        if (!response.ok) throw new Error('Product not found');

        const data = await response.json();
        imageUrl = data.product.image?.src || data.product.images?.[0]?.src;
        productTitle = data.product.title;

        // キャッシュに保存（画像URLとタイトル両方）
        productImageCache.set(productHandle, {
          imageUrl: imageUrl,
          title: productTitle
        });
      }

      if (imageUrl) {
        // 画像要素を作成
        const imgWrapper = document.createElement('div');
        imgWrapper.className = 'jdgm-rev__product-image';

        // 画像をリンクで囲む
        const imgLink = document.createElement('a');
        imgLink.href = review.dataset.productUrl || '#';

        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = productTitle || 'Product image';
        img.loading = 'lazy';

        imgLink.appendChild(img);
        imgWrapper.appendChild(imgLink);

        // レビューの最初に画像を追加
        review.insertBefore(imgWrapper, review.firstChild);

        // 商品名をヘッダーメタに追加
        const headerMeta = review.querySelector('.jdgm-rev__header-meta');
        if (headerMeta && productTitle) {
          // 商品名要素を作成
          const prodNameEl = document.createElement('div');
          prodNameEl.className = 'jdgm-rev__prod-name';

          const prodLink = document.createElement('a');
          prodLink.href = review.dataset.productUrl || '#';
          prodLink.textContent = productTitle;
          prodNameEl.appendChild(prodLink);

          // rating-dateコンテナの後に追加（rating-dateコンテナとは別の行）
          const ratingDateContainer = headerMeta.querySelector('.jdgm-rev__rating-date');
          if (ratingDateContainer && ratingDateContainer.nextSibling) {
            headerMeta.insertBefore(prodNameEl, ratingDateContainer.nextSibling);
          } else if (ratingDateContainer) {
            // rating-dateコンテナが最後の要素の場合、その後に追加
            headerMeta.appendChild(prodNameEl);
          } else {
            // rating-dateコンテナがない場合は先頭に追加
            headerMeta.insertBefore(prodNameEl, headerMeta.firstChild);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load product image:', error);
    }
  }

  /**
   * カスタムフィールドを並べ替え
   * 順序: 名前（投稿者） → SIZE | COLOR → 性別
   * 用途は非表示
   */
  function reorderCustomFields(review) {
    const customForm = review.querySelector('.jdgm-rev__custom-form');
    if (!customForm) return;

    const allFields = Array.from(customForm.querySelectorAll('.jdgm-rev__cf-ans'));

    let nameField = null;
    let sizeField = null;
    let colorField = null;
    let genderField = null;

    // 各フィールドを識別
    allFields.forEach(field => {
      const title = field.querySelector('.jdgm-rev__cf-ans__title');
      if (!title) return;

      const titleText = title.textContent.trim();

      if (titleText.includes('名前')) {
        nameField = field;
        // 「名前」を「投稿者」に変更
        title.textContent = '投稿者:';
      } else if (titleText.includes('SIZE')) {
        sizeField = field;
      } else if (titleText.includes('COLOR')) {
        colorField = field;
      } else if (titleText.includes('性別')) {
        genderField = field;
      } else if (titleText.includes('用途')) {
        // 用途は非表示
        field.style.display = 'none';
      }
    });

    // customForm を空にして再構築
    customForm.innerHTML = '';

    // 順序: 名前 → SIZE → COLOR → 性別
    if (nameField) {
      nameField.className = 'jdgm-rev__author-name';
      customForm.appendChild(nameField);
    }

    if (sizeField) {
      customForm.appendChild(sizeField);
    }

    if (colorField) {
      customForm.appendChild(colorField);
    }

    if (genderField) {
      customForm.appendChild(genderField);
    }
  }

  /**
   * 「続きを読む」ボタンを追加
   * レビュー本文が5行を超える場合に省略表示し、ボタンで展開/折りたたみ
   */
  function addReadMoreButton(bodyElement, contentContainer) {
    // 本文が5行を超えているかチェック
    // 一時的にline-clampを解除して高さを測定
    const lineHeight = parseFloat(window.getComputedStyle(bodyElement).lineHeight);
    const maxLines = 4;
    const maxHeight = lineHeight * maxLines;

    // 実際の高さを測定
    const actualHeight = bodyElement.scrollHeight;

    // 5行を超えている場合のみ処理
    if (actualHeight > maxHeight + 5) { // 5pxの余裕を持たせる
      // 省略表示にする
      bodyElement.classList.add('is-clamped');

      // 「続きを読む」ボタンを作成
      const readMoreBtn = document.createElement('span');
      readMoreBtn.className = 'jdgm-rev__read-more';
      readMoreBtn.textContent = '続きを読む';

      // ボタンをbodyの後に挿入
      bodyElement.insertAdjacentElement('afterend', readMoreBtn);

      // クリックイベント
      readMoreBtn.addEventListener('click', function() {
        if (bodyElement.classList.contains('is-clamped')) {
          // 展開
          bodyElement.classList.remove('is-clamped');
          readMoreBtn.textContent = '閉じる';
        } else {
          // 折りたたみ
          bodyElement.classList.add('is-clamped');
          readMoreBtn.textContent = '続きを読む';
        }
      });
    }
  }

  /**
   * レビューのレイアウトをカスタマイズ
   * ヘッダー: 画像 + メタ情報（レート、日付、商品名、COLOR、SIZE）
   * コンテンツ: タイトル + 本文 + 商品リンクボタン
   */
  function customizeReviewLayout(review) {
    // 既にカスタマイズ済みの場合はスキップ
    if (review.dataset.customized) return;

    // 必要な要素を取得（クローン前に取得）
    const prodName = review.querySelector('.jdgm-rev__prod-name');
    const rating = review.querySelector('.jdgm-rev__rating');
    const timestamp = review.querySelector('.jdgm-rev__timestamp');
    const title = review.querySelector('.jdgm-rev__title');
    const customForm = review.querySelector('.jdgm-rev__custom-form');
    const body = review.querySelector('.jdgm-rev__body');
    const prodLink = review.querySelector('.jdgm-rev__prod-link');

    // 商品リンクのURLを保存（画像取得用にdata属性にも保存）
    const productUrl = prodLink ? prodLink.href : null;
    if (productUrl) {
      // 商品ハンドルを抽出してdata属性に保存
      const urlParts = productUrl.split('/products/');
      if (urlParts.length >= 2) {
        const productHandle = urlParts[1].split('#')[0].split('?')[0];
        review.dataset.productHandle = productHandle;
        review.dataset.productUrl = productUrl;
      }
    }

    // ヘッダー部分（画像の右側に表示するメタ情報）
    const headerMeta = document.createElement('div');
    headerMeta.className = 'jdgm-rev__header-meta';

    // レートと日付を横並びにするコンテナ
    if (rating || timestamp) {
      const ratingDateContainer = document.createElement('div');
      ratingDateContainer.className = 'jdgm-rev__rating-date';

      if (rating) {
        const ratingClone = rating.cloneNode(true);
        const score = rating.dataset.score; // data-score属性から数値を取得

        if (score) {
          // 数値を表示するspan要素を作成（小数点なし）
          const scoreSpan = document.createElement('span');
          scoreSpan.className = 'jdgm-rev__rating-score-number';
          scoreSpan.textContent = ` ${Math.floor(parseFloat(score))}`; // 小数点なし
          ratingClone.appendChild(scoreSpan);
        }
        ratingDateContainer.appendChild(ratingClone);
      }
      if (timestamp) {
        ratingDateContainer.appendChild(timestamp.cloneNode(true));
      }

      headerMeta.appendChild(ratingDateContainer);
    }

    // 商品名を追加
    if (prodName) {
      headerMeta.appendChild(prodName.cloneNode(true));
    }

    // カスタムフィールドを追加
    if (customForm) {
      headerMeta.appendChild(customForm.cloneNode(true));
    }

    // コンテンツ部分（全幅で下に表示）
    const content = document.createElement('div');
    content.className = 'jdgm-rev__content';

    if (title) {
      // タイトルはテキストのみクローン（余計な子要素を含めない）
      const titleClone = document.createElement('span');
      titleClone.className = 'jdgm-rev__title';
      titleClone.textContent = title.textContent;
      content.appendChild(titleClone);
    }
    let bodyClone = null;
    if (body) {
      // 本文はテキストのみクローン（カスタムフォーム等の子要素を含めない）
      bodyClone = document.createElement('div');
      bodyClone.className = 'jdgm-rev__body';
      bodyClone.textContent = body.textContent;
      content.appendChild(bodyClone);
    }

    // 商品ページへのリンクボタン
    if (productUrl) {
      const linkButton = document.createElement('a');
      linkButton.href = productUrl;
      linkButton.className = 'view-more';
      linkButton.innerHTML = '商品を見る<svg class="icon-slide-right" id="icn_slide_right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20" height="30.724" viewBox="0 0 20 30.724"><defs><clipPath id="clip-path"><rect id="長方形_17" data-name="長方形 17" width="20" height="30.724" fill="none"></rect></clipPath></defs><g id="グループ_19" data-name="グループ 19" clip-path="url(#clip-path)"><path id="パス_12" data-name="パス 12" d="M14.4,15.362,0,.961a3.275,3.275,0,0,1,4.633,0l14.4,14.4-14.4,14.4a3.276,3.276,0,0,1-4.633,0v0Z" transform="translate(0.963 0)" fill="#f6b79e"></path></g></svg>';
      content.appendChild(linkButton);
    }

    // 元の子要素を全て削除
    while (review.firstChild) {
      review.removeChild(review.firstChild);
    }

    // 新しい構造を追加
    review.appendChild(headerMeta);
    review.appendChild(content);

    review.dataset.customized = 'true';

    // DOMに追加された後、次のフレームで「続きを読む」機能を追加
    if (bodyClone) {
      requestAnimationFrame(() => {
        addReadMoreButton(bodyClone, content);
      });
    }

    // Judge.meが後からcontent内にcustomFormを追加する可能性があるため、
    // MutationObserverで監視して削除
    const contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 &&
              (node.classList?.contains('jdgm-rev__custom-form') ||
               node.querySelector?.('.jdgm-rev__custom-form'))) {
            // content内のcustomFormを削除
            const unwantedForms = content.querySelectorAll('.jdgm-rev__custom-form');
            unwantedForms.forEach(form => form.remove());
          }
        });
      });
    });

    contentObserver.observe(content, {
      childList: true,
      subtree: true
    });

    // 既存のcontent内のcustomFormを即座に削除
    const existingForms = content.querySelectorAll('.jdgm-rev__custom-form');
    if (existingForms.length > 0) {
      existingForms.forEach(form => form.remove());
    }

    // header-metaにcustomFormが存在することを確認し、なければ追加
    setTimeout(() => {
      const headerMetaCheck = review.querySelector('.jdgm-rev__header-meta');
      const contentCheck = review.querySelector('.jdgm-rev__content');

      if (headerMetaCheck && customForm) {
        const existingCustomForm = headerMetaCheck.querySelector('.jdgm-rev__custom-form');
        if (!existingCustomForm) {
          const newCustomFormClone = customForm.cloneNode(true);
          headerMetaCheck.appendChild(newCustomFormClone);
        }
      }

      // content内のcustomFormを再度削除
      if (contentCheck) {
        const unwantedForms = contentCheck.querySelectorAll('.jdgm-rev__custom-form');
        if (unwantedForms.length > 0) {
          unwantedForms.forEach(form => form.remove());
        }
      }

      // 日付を yyyy/mm/dd 形式に変換
      const timestampElement = review.querySelector('.jdgm-rev__timestamp');
      if (timestampElement) {
        const dateContent = timestampElement.dataset.content || timestampElement.getAttribute('data-content');
        if (dateContent) {
          const date = new Date(dateContent);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          timestampElement.textContent = `${year}/${month}/${day}`;
        }
      }
    }, 100);
  }

  /**
   * Swiperを初期化
   */
  function initSwiper() {
    const widget = document.querySelector(WIDGET_SELECTOR);

    if (!widget) {
      console.warn('Widget not found');
      return;
    }

    // 既にSwiper化されている場合はスキップ
    if (widget.classList.contains('swiper-initialized')) {
      return;
    }

    // widgetをSwiperコンテナとして使用
    const reviewsContainer = widget;

    // 表示されているレビューのみを取得（data-filtered="visible"）
    const visibleReviews = Array.from(reviewsContainer.querySelectorAll('.jdgm-rev[data-filtered="visible"]'));

    if (visibleReviews.length === 0) {
      return;
    }

    // Swiperコンテナに変換
    reviewsContainer.classList.add('swiper');

    // スライドラッパーを作成
    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    // 表示されているレビューのみをスライドに変換
    visibleReviews.forEach(review => {
      review.classList.add('swiper-slide');
      swiperWrapper.appendChild(review);
    });

    // 非表示のレビューは削除
    const allReviews = Array.from(reviewsContainer.querySelectorAll('.jdgm-rev'));
    allReviews.forEach(review => {
      if (review.dataset.filtered !== 'visible') {
        review.remove();
      }
    });

    reviewsContainer.appendChild(swiperWrapper);

    // ナビゲーション要素を追加
    // const prevButton = document.createElement('div');
    // prevButton.className = 'swiper-button-prev swiper-button-reviews';
    // reviewsContainer.appendChild(prevButton);
    //
    // const nextButton = document.createElement('div');
    // nextButton.className = 'swiper-button-next swiper-button-reviews';
    // reviewsContainer.appendChild(nextButton);

    // スクロールバーを追加
    const scrollbar = document.createElement('div');
    scrollbar.className = 'swiper-scrollbar swiper-scrollbar-reviews';
    reviewsContainer.appendChild(scrollbar);

    // Swiperを初期化してグローバル変数に保存
    swiperInstance = new Swiper(reviewsContainer, {
      slidesPerView: 1.2, // モバイル: 1枚 + 次のカードが20%見える
      spaceBetween: 15,
      freeMode: true,
      watchSlidesProgress: true,
      navigation: {
        nextEl: '.swiper-button-reviews.swiper-button-next',
        prevEl: '.swiper-button-reviews.swiper-button-prev',
      },
      scrollbar: {
        el: '.swiper-scrollbar-reviews',
        draggable: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 2.2, // タブレット・デスクトップ: 2枚表示
          spaceBetween: 20,
        }
      }
    });
  }

  /**
   * MutationObserverでDOMの変更を監視
   */
  function observeReviews() {
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) return;

    const observer = new MutationObserver((mutations) => {
      // 新しいレビューが追加されたらフィルタリング実行
      const hasNewReviews = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          return node.nodeType === 1 &&
                 (node.matches(REVIEW_SELECTOR) || node.querySelector(REVIEW_SELECTOR));
        });
      });

      if (hasNewReviews) {
        filterAndLimitReviews();
      }
    });

    observer.observe(widget, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 初期化処理
   */
  function init() {
    const intervalId = setInterval(() => {
      attempts++;

      if (filterAndLimitReviews()) {
        clearInterval(intervalId);
        isProcessed = true;

        // Swiperを初期化（レビューフィルタリング後）
        initSwiper();

        observeReviews();
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(intervalId);
      }
    }, CHECK_INTERVAL);
  }

  // DOMContentLoadedで初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();